import React, { useState, useRef, useEffect } from 'react';
import './Chat.css';
import { useAppMessage, useLocalParticipant, useDaily } from '@daily-co/daily-react';
import { DailyEventObjectAppMessage } from '@daily-co/daily-js';
import { ChatAppMessage, AppMessage } from '../../../babylon/World/Types';
import Input from '../../primitives/Input/Input';
import Button from '../../primitives/Button/Button';

import { ReactComponent as SendIcon } from '../../../../assets/icon/caret-chevron-right.svg';
import { useChat } from '../../context/ChatContext';

interface ChatMessage {
  from: string;
  message: string;
}

const HUDChat = () => {
  const { messages, addMessage } = useChat();
  const [newMessage, setNewMessage] = useState<string>('');
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);

  const sendAppMessage = useAppMessage({ onAppMessage: (ev: DailyEventObjectAppMessage) => handleAppMessage(ev)});
  const localParticipant = useLocalParticipant();
  const callObject = useDaily();

  const handleAppMessage = (dailyAppMessage: DailyEventObjectAppMessage) => {
    if (dailyAppMessage.data.messageType !== 'world' || dailyAppMessage.data.worldMessageType !== 'chat-message') return;
    const chatMessage: ChatMessage = {
      from: dailyAppMessage.data.userName,
      message: dailyAppMessage.data.chatMessage,
    };

    addMessage(chatMessage);
  }

  const submitMessage = (message: string) => {
    if (!localParticipant) return console.error('not connected to room, cannot send chat message');
    if (message.trim() === '') return;
    const chatMessage: ChatAppMessage = {
      messageType: 'world',
      worldMessageType: 'chat-message',
      userName: localParticipant.user_name,
      chatMessage: message,
    }
    if (callObject?.meetingState() === 'joined-meeting') sendAppMessage(chatMessage);

    const localChatMessage: ChatMessage = {
      from: localParticipant.user_name,
      message: newMessage,
    };
    addMessage(localChatMessage);
    setNewMessage('');
    if (chatInputRef.current) chatInputRef.current.focus();
  }

  // This is O(n) in the worst case, do better
  const shouldShowUserName = (index: number) => {
    const numMessagesFromSameUserBeforeShowing = 4;
    let consecutiveCount = 0;
    for (let i = index; i > 0; i--) {
      if (messages[i - 1].from === messages[index].from) {
        consecutiveCount++;
      } else {
        break;
      }
    }
    return (consecutiveCount === 0 || (consecutiveCount + 1) % (numMessagesFromSameUserBeforeShowing + 1) === 1);
  }

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, []);

  return (
    <>
      <div className="chat-messages">
        {messages.map((msg, index) => {
          const showUserName =
            shouldShowUserName(index);
          return (
            <div key={index} className="chat-message">
              {showUserName && <span className="chat-message-user">{msg.from}: </span>}
              <span className="chat-message-text">{msg.message}</span>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>
      <div className="chat-input">
        <Input ref={chatInputRef}
               initialValue={newMessage}
               onChange={setNewMessage}
               onSubmit={submitMessage}
        />
        <Button icon={<SendIcon />}
                onClick={() => submitMessage(newMessage)}
        />
      </div>
  </>  
  );
};

export default HUDChat;
